// ==========================================
// Save/load helper factory.
//
// Every engine had a near-identical 12-line block exporting save/load/
// hasSavedGame/deleteSave functions that differed only in the SAVE_KEY
// localStorage key. ~180 LOC of pure ceremony across 15 engines.
//
// Engines now do:
//   export const { save: saveX, load: loadX, has: hasXSavedGame,
//                  remove: deleteXSave } = makeSaveStore("chronicles_europe_save_x");
//
// Behaviour is identical: serialize the Set<string> flags as an array,
// timestamp, JSON.stringify; on load, parse, validate that statuses
// exists, and rehydrate the Set. Corrupt entries are wiped silently.
// ==========================================

import type { GameState, SaveData } from "./types";

export interface SaveStore {
  save: (state: GameState) => void;
  load: () => GameState | null;
  has: () => boolean;
  remove: () => void;
}

export function makeSaveStore(saveKey: string): SaveStore {
  return {
    save(state: GameState): void {
      const data: SaveData = {
        ...state,
        flags: Array.from(state.flags),
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(saveKey, JSON.stringify(data));
    },

    load(): GameState | null {
      const raw = localStorage.getItem(saveKey);
      if (!raw) return null;
      try {
        const data = JSON.parse(raw);
        if (!data.statuses) {
          localStorage.removeItem(saveKey);
          return null;
        }
        return { ...data, flags: new Set(data.flags) } as GameState;
      } catch {
        localStorage.removeItem(saveKey);
        return null;
      }
    },

    has(): boolean {
      // Skip zero-decision non-complete saves — the Play page used
      // to autosave the initial GameState on mount, before any
      // choice was committed. Treating those as "saved progress"
      // made the ResumePrompt resurface "Continue your chronicle?"
      // on fresh first-time visits, which is the exact bug players
      // describe as "it asks me to continue at decision 0 even on
      // a fresh game". Mirror the same filter useLastPlayedChronicle
      // applies to keep both surfaces consistent. Also wipe the
      // stale entry on the way out so players upgrading from an
      // older build don't carry zero-decision saves forward.
      const raw = localStorage.getItem(saveKey);
      if (!raw) return false;
      try {
        const data = JSON.parse(raw);
        if (!data.statuses) {
          localStorage.removeItem(saveKey);
          return false;
        }
        const decisions = Array.isArray(data.choiceHistory) ? data.choiceHistory.length : 0;
        if (decisions === 0 && data.isComplete !== true) {
          localStorage.removeItem(saveKey);
          return false;
        }
        return true;
      } catch {
        localStorage.removeItem(saveKey);
        return false;
      }
    },

    remove(): void {
      localStorage.removeItem(saveKey);
    },
  };
}
